import React, { useCallback, useMemo, useEffect, memo } from 'react';
import { ProjectDetailsForm } from '@/components/Forms/ProjectDetailsForm';
import { RangeForm } from '@/components/TargetCost/Forms/RangeForm';
import { CostsForm } from '@/components/Forms/CostsForm';
import { MetricsGrid } from '@/components/TargetCost/Metrics/MetricsGrid';
import { CostComparisonChart } from '@/components/TargetCost/Charts/CostComparisonChart';
import { ShareDistributionChart } from '@/components/TargetCost/Charts/ShareDistributionChart';
import { Container } from '@/components/Layout/Container';
import { SecondaryHeader } from '@/components/Layout/Header/SecondaryHeader';
import { Sidebar } from '@/components/shared/Sidebar';
import { useTargetCost } from '@/context/TargetCostContext';
import { useSidebar } from '@/context/SidebarContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TargetCostData } from '@/types/target-cost';
import { captureEvent, EVENTS, TOOL_IDS, TOOL_NAMES } from '@/lib/posthog';
import { saveTargetCostCalculation } from '@/lib/supabase/target-cost';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { SavedCalculation } from '@/types/cvr';

// Memoize static components to prevent unnecessary re-renders
const MemoizedSecondaryHeader = memo(SecondaryHeader);

export function TargetCostTool() {
  const { state, dispatch } = useTargetCost();
  const { isCollapsed } = useSidebar();
  const { toast } = useToast();
  const { data } = state;

  // Track tool view on mount
  useEffect(() => {
    captureEvent(EVENTS.TOOL_VIEWED, {
      tool_id: TOOL_IDS.TARGET_COST,
      tool_name: TOOL_NAMES[TOOL_IDS.TARGET_COST],
    });
  }, []);

  const handleSave = useCallback(
    async (name: string, saveData: unknown) => {
      try {
        if (!name?.trim()) {
          throw new Error('Calculation name is required');
        }

        if (!saveData || typeof saveData !== 'object') {
          throw new Error('Invalid calculation data');
        }

        const savedRecord = await saveTargetCostCalculation(name, saveData as TargetCostData);

        captureEvent(EVENTS.TARGET_COST_SAVED, {
          tool_id: TOOL_IDS.TARGET_COST,
          tool_name: TOOL_NAMES[TOOL_IDS.TARGET_COST],
          project_name: name,
        });

        dispatch({ type: 'MARK_SAVED' });

        toast({
          title: 'Success',
          description: 'Calculation saved successfully',
        });

        return savedRecord;
      } catch (error) {
        toast({
          title: 'Error',
          description:
            error instanceof Error ? error.message : 'Failed to save calculation',
          variant: 'destructive',
        });
        throw error;
      }
    },
    [dispatch, toast]
  );

  const handleLoad = useCallback(
    (calculation: SavedCalculation) => {
      try {
        if (!calculation?.data) {
          throw new Error('Invalid calculation data');
        }

        dispatch({
          type: 'UPDATE_DATA',
          payload: calculation.data as TargetCostData,
        });

        captureEvent(EVENTS.TARGET_COST_LOADED, {
          tool_id: TOOL_IDS.TARGET_COST,
          tool_name: TOOL_NAMES[TOOL_IDS.TARGET_COST],
          calculation_id: calculation.id,
        });

        toast({
          title: 'Success',
          description: 'Calculation loaded successfully',
        });
      } catch (error) {
        toast({
          title: 'Error',
          description:
            error instanceof Error ? error.message : 'Failed to load calculation',
          variant: 'destructive',
        });
      }
    },
    [dispatch, toast]
  );

  /**
   * A strongly typed update handler.
   * This function is used to update various sections of the global data
   * (e.g. "details", "costs", "ranges"). Notably, the CostsForm component
   * automatically calculates *Savings* and *Cost Overrun* based on user input,
   * and then calls this handler to update the global state.
   *
   * @param field - The key of the section being updated.
   * @param updates - The new values to merge into that section.
   */
  const handleUpdate = useCallback(
    <K extends keyof TargetCostData>(
      field: K,
      updates: Partial<TargetCostData[K]>
    ) => {
      dispatch({
        type: 'UPDATE_DATA',
        payload: {
          ...data,
          [field]: {
            ...data[field],
            ...updates,
          },
        },
      });
    },
    [data, dispatch]
  );

  // Memoize derived values for the RangeForm.
  const targetPrice = useMemo(() => data.costs?.totalPrices ?? 0, [data.costs]);
  const actualCostPct = useMemo(() => {
    if (data.costs?.finalPWDD != null) {
      return (data.costs.finalPWDD / (targetPrice || 1)) * 100;
    }
    return 0;
  }, [data.costs, targetPrice]);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <MemoizedSecondaryHeader className="px-4 sm:px-6" />

      <div className="flex flex-1 flex-col md:flex-row">
        <Sidebar
          toolType="target-cost"
          data={data}
          onSave={handleSave}
          onLoad={handleLoad}
          className={cn(
            'w-full overflow-y-auto border-b transition-all duration-300',
            'md:border-r md:border-b-0',
            isCollapsed ? 'md:w-20' : 'md:w-80'
          )}
          aria-label="Target Cost calculation sidebar"
          role="complementary"
        />

        <MainContent
          data={data}
          handleUpdate={handleUpdate}
          targetPrice={targetPrice}
          actualCostPct={actualCostPct}
          isCollapsed={isCollapsed}
        />
      </div>
    </div>
  );
}

interface MainContentProps {
  data: TargetCostData;
  handleUpdate: <K extends keyof TargetCostData>(
    field: K,
    updates: Partial<TargetCostData[K]>
  ) => void;
  targetPrice: number;
  actualCostPct: number;
  isCollapsed: boolean;
}

const MainContent = memo(({
  data,
  handleUpdate,
  targetPrice,
  actualCostPct,
  isCollapsed
}: MainContentProps) => (
  <main
    className={cn(
      'flex-1 overflow-y-auto transition-all duration-300',
      isCollapsed ? 'md:ml-20' : 'md:ml-80'
    )}
    role="main"
  >
    <Container className="px-4 sm:px-6">
      <div className="space-y-6 py-8">
        {/* Project Details Section */}
        <Card>
          <CardHeader>
            <CardTitle>Project Details</CardTitle>
          </CardHeader>
          <CardContent>
            <ProjectDetailsForm
              details={data.details}
              onChange={(updates) => handleUpdate('details', updates)}
            />
          </CardContent>
        </Card>

        {/* Target and Actual Costs Section */}
        <Card>
          <CardHeader>
            <CardTitle>Target and Actual Costs</CardTitle>
          </CardHeader>
          <CardContent>
            <CostsForm
              costs={data.costs}
              onChange={(updates) => handleUpdate('costs', updates)}
            />
          </CardContent>
        </Card>

        {/* Pain/Gain Share Ranges */}
        <RangeForm
          ranges={data.ranges}
          targetPrice={targetPrice}
          actualCostPct={actualCostPct}
          onChange={(updates) => handleUpdate('ranges', updates)}
        />

        {/* Metrics Grid */}
        <MetricsGrid data={data} />

        {/* Cost Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CostComparisonChart data={data} />
          <ShareDistributionChart data={data} />
        </div>
      </div>
    </Container>
  </main>
));
